export type OfflineQueueMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface OfflineQueueItem {
  id: string;
  type: string;
  method: OfflineQueueMethod;
  url: string;
  body?: unknown;
  createdAt: string;
  attempts: number;
  lastError?: string;
}

export interface OfflineQueueMeta {
  lastAttemptAt?: string;
  lastSyncedAt?: string;
}

export interface OfflineQueueSummary extends OfflineQueueMeta {
  count: number;
  failedCount: number;
  lastError?: string;
  items: OfflineQueueItem[];
}

const DB_NAME = 'heims-offline';
const DB_VERSION = 1;
const OUTBOX_STORE = 'outbox';
const OFFLINE_QUEUE_CHANGED = 'heims:offline-queue-changed';
const OFFLINE_QUEUE_META_KEY = 'heimsOfflineQueueMeta';

function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const store = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt');
        store.createIndex('type', 'type');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function emitQueueChanged() {
  window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_CHANGED));
}

export function subscribeToOfflineQueue(listener: () => void) {
  window.addEventListener(OFFLINE_QUEUE_CHANGED, listener);
  return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED, listener);
}

export async function addOfflineQueueItem(
  item: Omit<OfflineQueueItem, 'id' | 'createdAt' | 'attempts'>,
) {
  const db = await openOfflineDb();
  const queueItem: OfflineQueueItem = {
    ...item,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    attempts: 0,
  };

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(OUTBOX_STORE, 'readwrite');
    transaction.objectStore(OUTBOX_STORE).add(queueItem);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

  db.close();
  emitQueueChanged();
  return queueItem;
}

export async function listOfflineQueueItems(): Promise<OfflineQueueItem[]> {
  const db = await openOfflineDb();

  const items = await new Promise<OfflineQueueItem[]>((resolve, reject) => {
    const transaction = db.transaction(OUTBOX_STORE, 'readonly');
    const request = transaction.objectStore(OUTBOX_STORE).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  db.close();
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function getOfflineQueueCount(): Promise<number> {
  const db = await openOfflineDb();

  const count = await new Promise<number>((resolve, reject) => {
    const transaction = db.transaction(OUTBOX_STORE, 'readonly');
    const request = transaction.objectStore(OUTBOX_STORE).count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  db.close();
  return count;
}

export function getOfflineQueueMeta(): OfflineQueueMeta {
  const raw = localStorage.getItem(OFFLINE_QUEUE_META_KEY);
  if (!raw) return {};

  try {
    return JSON.parse(raw) as OfflineQueueMeta;
  } catch {
    localStorage.removeItem(OFFLINE_QUEUE_META_KEY);
    return {};
  }
}

export function updateOfflineQueueMeta(meta: OfflineQueueMeta): OfflineQueueMeta {
  const nextMeta = {
    ...getOfflineQueueMeta(),
    ...meta,
  };
  localStorage.setItem(OFFLINE_QUEUE_META_KEY, JSON.stringify(nextMeta));
  emitQueueChanged();
  return nextMeta;
}

export async function getOfflineQueueSummary(): Promise<OfflineQueueSummary> {
  const items = await listOfflineQueueItems();
  const failedItems = items.filter((item) => item.lastError);
  const meta = getOfflineQueueMeta();

  return {
    ...meta,
    count: items.length,
    failedCount: failedItems.length,
    lastError: failedItems.at(-1)?.lastError,
    items,
  };
}

export async function removeOfflineQueueItem(id: string): Promise<void> {
  const db = await openOfflineDb();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(OUTBOX_STORE, 'readwrite');
    transaction.objectStore(OUTBOX_STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

  db.close();
  emitQueueChanged();
}

export async function markOfflineQueueItemFailed(id: string, lastError: string): Promise<void> {
  const db = await openOfflineDb();

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(OUTBOX_STORE, 'readwrite');
    const store = transaction.objectStore(OUTBOX_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const item = request.result as OfflineQueueItem | undefined;
      if (!item) return;

      store.put({
        ...item,
        attempts: item.attempts + 1,
        lastError,
      });
    };

    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });

  db.close();
  emitQueueChanged();
}
